import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { ApiError, atlas, type SearchResponse } from '../lib/api';

type SearchState = 'idle' | 'focused' | 'composing' | 'loading' | 'results' | 'empty' | 'error';

const DEBOUNCE_MS = 180;

/**
 * Knowledge-first global search.
 *
 * Three behaviours here are easy to get wrong and are the reason this is not a plain
 * debounced input:
 *
 *  - While an IME composition is active the buffer holds partial jamo or pinyin, so
 *    searching it produces nonsense results and steals focus mid-word. Nothing is sent
 *    until composition ends.
 *  - Every keystroke supersedes the previous request. The old one is aborted AND its
 *    sequence number is checked on arrival, so a slow earlier response can never
 *    overwrite a newer one that already rendered.
 *  - Results are knowledge first. Raw text hits arrive in their own field and are
 *    rendered under their own heading, never blended in as though they were curated.
 */
export function GlobalSearch() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [query, setQuery] = useState('');
  const [state, setState] = useState<SearchState>('idle');
  const [response, setResponse] = useState<SearchResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [composing, setComposing] = useState(false);

  const requestSeq = useRef(0);
  const renderedSeq = useRef(0);
  const inFlight = useRef<AbortController | null>(null);

  const results = useMemo(() => {
    if (!response) return [] as { url: string; key: string }[];
    return [
      ...response.knowledge.map((entry) => ({ url: entry.node.canonicalUrl, key: entry.node.id })),
      ...response.documentMatches.map((match) => ({ url: match.canonicalUrl, key: match.sectionId })),
    ];
  }, [response]);

  const runSearch = useCallback(async (value: string) => {
    const trimmed = value.trim();
    inFlight.current?.abort();

    if (trimmed.length === 0) {
      setResponse(null);
      setState('focused');
      return;
    }

    const seq = ++requestSeq.current;
    const controller = new AbortController();
    inFlight.current = controller;
    setState('loading');

    try {
      const result = await atlas.search(trimmed, controller.signal);
      // A response that is not the newest one is discarded even though it succeeded.
      if (seq < renderedSeq.current) return;
      renderedSeq.current = seq;
      setResponse(result);
      setActiveIndex(0);
      setState(result.knowledge.length === 0 && result.documentMatches.length === 0 ? 'empty' : 'results');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      if (seq < renderedSeq.current) return;
      renderedSeq.current = seq;
      setErrorMessage(error instanceof ApiError ? error.message : 'Search is unavailable.');
      setState('error');
    }
  }, []);

  useEffect(() => {
    if (composing) {
      setState('composing');
      return;
    }
    const timer = setTimeout(() => void runSearch(query), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, composing, runSearch]);

  // Cmd/Ctrl+K from anywhere, matching the accepted design's affordance.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setState('idle');
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  const open = state !== 'idle';

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Arrow keys during composition belong to the IME candidate list, not to us.
    if (composing) return;
    if (event.key === 'Escape') {
      setState('idle');
      inputRef.current?.blur();
      return;
    }
    if (results.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % results.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + results.length) % results.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const target = results[activeIndex];
      if (target) {
        setState('idle');
        void navigate({ to: target.url });
      }
    }
  };

  return (
    <div className="global-search-container" ref={containerRef}>
      <div className="search-input-wrapper" id="search-input-wrapper">
        <span className="search-icon" aria-hidden="true">
          <Search size={14} />
        </span>
        <input
          ref={inputRef}
          id="global-search-input"
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls="atlas-search-results"
          aria-autocomplete="list"
          aria-label="Search Atlas"
          placeholder="Search Atlas (Cmd+K)..."
          autoComplete="off"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onFocus={() => setState((prev) => (prev === 'idle' ? 'focused' : prev))}
          onCompositionStart={() => setComposing(true)}
          onCompositionEnd={(event) => {
            setComposing(false);
            setQuery(event.currentTarget.value);
          }}
          onKeyDown={onKeyDown}
        />
        <span className="search-shortcut" aria-hidden="true">
          ⌘K
        </span>
      </div>

      {open ? (
        <div className="search-dropdown" id="atlas-search-results" role="listbox">
          {state === 'focused' && query.length === 0 ? (
            <div className="search-state-msg">Search knowledge, then its supporting documents.</div>
          ) : null}
          {state === 'composing' ? <div className="search-state-msg">Typing…</div> : null}
          {state === 'loading' ? <div className="search-state-msg">Searching graph…</div> : null}
          {state === 'empty' ? (
            <div className="search-state-msg">
              No knowledge or admitted document matched “{query.trim()}”.
            </div>
          ) : null}
          {state === 'error' ? <div className="search-state-msg error-text">{errorMessage}</div> : null}

          {state === 'results' && response ? (
            <SearchResults response={response} activeIndex={activeIndex} onPick={() => setState('idle')} />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function SearchResults({
  response,
  activeIndex,
  onPick,
}: {
  response: SearchResponse;
  activeIndex: number;
  onPick: () => void;
}) {
  const navigate = useNavigate();
  const go = (url: string) => {
    onPick();
    void navigate({ to: url });
  };

  return (
    <>
      {response.knowledge.map((entry, index) => (
        <div
          key={entry.node.id}
          className={`search-result-item${index === activeIndex ? ' selected' : ''}`}
          role="option"
          aria-selected={index === activeIndex}
          tabIndex={0}
          onClick={() => go(entry.node.canonicalUrl)}
          onKeyDown={(event) => event.key === 'Enter' && go(entry.node.canonicalUrl)}
        >
          <div className="res-title">
            <span>
              <span className="kg-icon" aria-hidden="true">
                🧠
              </span>{' '}
              {entry.node.title}
            </span>
            <span className="res-badge concept-badge">{entry.node.kind}</span>
          </div>
          {entry.node.summary ? <div className="res-context">{entry.node.summary}</div> : null}

          {entry.relationships.length > 0 ? (
            <div className="res-context" style={{ marginTop: 4 }}>
              {entry.relationships.slice(0, 3).map((relation) => (
                <span key={`${relation.node.id}:${relation.type}`} className="relation">
                  {relation.type} → {relation.node.title}{' '}
                </span>
              ))}
            </div>
          ) : null}

          {/* Supporting documents stay collapsed beneath the knowledge they support. */}
          {entry.evidence.length > 0 ? (
            <details className="evidence-details">
              <summary>
                {entry.evidence.length} supporting {entry.evidence.length === 1 ? 'document' : 'documents'}
              </summary>
              <div className="evidence-list">
                {entry.evidence.slice(0, 5).map((evidence) => (
                  <div className="evidence-item" key={evidence.sectionId}>
                    <div className="ev-title">
                      {evidence.title} <span className="ev-badge">DocumentSection</span>
                    </div>
                    <div className="ev-path">
                      {evidence.sourcePath}
                      {evidence.sectionAnchor ? `#${evidence.sectionAnchor}` : ''}
                    </div>
                    {evidence.snippet ? <div className="ev-snippet">{evidence.snippet}</div> : null}
                  </div>
                ))}
              </div>
            </details>
          ) : null}
        </div>
      ))}

      {/*
        The fallback is labelled and visually separated. A term occurring in a file is
        not curated knowledge, and the UI must not let it look like it is.
      */}
      {response.documentMatches.length > 0 ? (
        <>
          <div
            className="search-state-msg"
            style={{ textAlign: 'left', fontWeight: 600, borderTop: '1px solid var(--border)' }}
          >
            Document matches (no knowledge entity matched)
          </div>
          {response.documentMatches.map((match, index) => {
            const position = response.knowledge.length + index;
            return (
              <div
                key={match.sectionId}
                className={`search-result-item${position === activeIndex ? ' selected' : ''}`}
                role="option"
                aria-selected={position === activeIndex}
                tabIndex={0}
                onClick={() => go(match.canonicalUrl)}
                onKeyDown={(event) => event.key === 'Enter' && go(match.canonicalUrl)}
              >
                <div className="res-title">
                  <span>{match.title}</span>
                  <span className="res-badge">Text match</span>
                </div>
                <div className="res-path">
                  {match.sourcePath}
                  {match.sectionAnchor ? `#${match.sectionAnchor}` : ''}
                </div>
                <div className="res-context">{match.snippet}</div>
              </div>
            );
          })}
        </>
      ) : null}
    </>
  );
}
